<script lang="ts">
  import { balances } from "$lib/stores";
  import type { IBalance, INetwork } from "$lib/interfaces/IBalance";
  import { onMount } from "svelte";
  import CustomDropdown from "$lib/elements/CurrencyDropdown.svelte";
  import Button from "$lib/elements/Button.svelte";
  import NumberFormat from "$lib/components/NumberFormat.svelte";
  import { z } from "zod";
  import { createForm } from "felte";
  import { validator } from "@felte/validator-zod";
  import { environment } from "$lib/env";
  import { getCookie } from "$lib/utils/cookies";
  import { locale, translate } from '$lib/i18n';
  import { toasts } from "$lib/stores/toastStores";
  import { userService } from "$lib/services/user.service";
  import { openDialog } from "$lib/stores/dialogStores";

  // Constants
  const USDT_VARIANTS = ['USDTE', 'USDTT'];
  const LOADING_DELAY = 1000;

  // State
  let storedBalances = $state<IBalance[]>([]);
  let selectedBalance = $state<IBalance | null>(null);
  let selectedNetwork = $state<INetwork | null>(null);
  let loading = $state(true);
  let apiError = $state('');
  let fundsLoading = $state(false);
  let initialized = $state(false);
  
  let withdrawableFunds = $state({ 
    lockedFunds: 0, 
    availableFunds: 0 
  });

  // Helper function to get USDT networks from USDTE and USDTT
  function getUSDTNetworks(balances: IBalance[]): INetwork[] {
    return USDT_VARIANTS
      .map(variant => balances.find(b => b.shortName === variant))
      .filter((balance): balance is IBalance => balance !== undefined)
      .flatMap(balance => balance.cryptoNetworks || []);
  }

  // Helper function to get network display name
  function getNetworkDisplayName(network: INetwork | undefined): string {
    if (!network || !selectedBalance) return '';
    
    if (selectedBalance.name === "USDT") {
      return network.name === "ETH" ? "ERC20" : "TRC20";
    }
    return network.name; 
  }

  // Define schema with Zod instead of Yup
  const schema = z.object({
    cryptoName: z.string().min(1, "Cryptocurrency is required"),
    cryptoNetworkName: z.string().min(1, "Network is required"),
    amount: z.number().positive("Amount must be positive"),
    address: z.string().min(1, "Address is required"),
    tag: z.string().optional(),
  });

  // Initialize form with Felte and Zod
  const { form, errors, isValid, createSubmitHandler } = createForm({
    initialValues: {
      cryptoName: '',
      cryptoNetworkName: '',
      amount: 0,
      address: '',
      tag: ''
    },
    validate: validator({ schema }),
    onSubmit: values => {
      // Form submission is handled by handleSubmit function
    }
  });

  // Form values
  let withdrawForm = $state({
    cryptoName: '',
    cryptoNetworkName: '',
    amount: 0,
    address: '',
    tag: ''
  });

  function clearForm() {
    withdrawForm.tag = '';
    withdrawForm.amount = 0;
    withdrawForm.address = '';
  }

  // Initialize balances only once
  onMount(() => {
    initializeBalances();
  });

  // Process balances only once on initialization
  function initializeBalances() {
    if (initialized) return;
    
    const balancesValue = $balances;
    if (!balancesValue || balancesValue.length === 0) return;

    try {
      // Filter out USDTE and USDTT from display list
      const filteredBalances = balancesValue.filter(balance => 
        balance.isCrypto && 
        !USDT_VARIANTS.includes(balance.shortName)
      );
      
      storedBalances = filteredBalances;

      // Find USDT and inject networks from USDTE/USDTT
      const usdtBalance = storedBalances.find(b => b.name === "USDT");
      if (usdtBalance) {
        const usdteBalance = balancesValue.find(b => b.shortName === 'USDTE');
        const usdttBalance = balancesValue.find(b => b.shortName === 'USDTT');
        
        // Combine networks
        usdtBalance.cryptoNetworks = getUSDTNetworks(balancesValue);
        
        // Set the balance from the respective network variant
        if (usdteBalance) {
          usdtBalance.balance = usdteBalance.balance;
          usdtBalance.fiatEquivalent = usdteBalance.fiatEquivalent;
        }
      }

      // Initialize selected balance and network safely
      if (storedBalances.length > 0) {
        const firstBalance = storedBalances[0];
        selectedBalance = firstBalance;
        
        if (firstBalance?.cryptoNetworks?.length > 0) {
          const firstNetwork = firstBalance.cryptoNetworks[0];
          selectedNetwork = firstNetwork;
          
          // Set form values
          withdrawForm.cryptoName = firstBalance.name;
          withdrawForm.cryptoNetworkName = firstNetwork.name;
        }
      }

      initialized = true;
      
      // Delay loading and funds check
      setTimeout(() => {
        loading = false;
        
        // Fetch funds after a delay
        if (selectedBalance) {
          setTimeout(() => {
            checkFunds(selectedBalance);
          }, 500);
        }
      }, LOADING_DELAY);
    } catch (error) {
      console.error('Error processing balances:', error);
      toasts.send('Error loading cryptocurrencies', 'error');
      loading = false;
      initialized = true; // Mark as initialized even on error to prevent retries
    }
  }

  function selectBalance(balance: IBalance) {
    if (!balance || loading) return;
    
    loading = true;
    clearForm();
    
    // Use a local variable to avoid reactivity issues
    const newBalance = balance;
    selectedBalance = newBalance;
    
    if (newBalance.cryptoNetworks?.length > 0) {
      const newNetwork = newBalance.cryptoNetworks[0];
      selectedNetwork = newNetwork;
      
      withdrawForm.cryptoName = newBalance.name;
      withdrawForm.cryptoNetworkName = newNetwork.name;
      
      // Delay funds check to avoid reactivity loop
      setTimeout(() => {
        loading = false;
        checkFunds(newBalance);
      }, 100);
    } else {
      loading = false;
    }
  }

  function selectNetwork(network: INetwork) {
    if (!network || loading) return;
    
    loading = true;
    clearForm();
    
    // Use local variables to avoid reactivity issues
    const currentBalance = selectedBalance;
    let newNetwork = network;
    
    if (currentBalance?.name === 'USDT') {
      // Get the correct variant balance with fees
      const variant = network.name === 'ETH' ? 'USDTE' : 'USDTT';
      const variantBalance = $balances?.find(b => b.shortName === variant);
      
      if (variantBalance?.cryptoNetworks?.[0]) {
        // Update the selected network with the correct fees from the variant
        newNetwork = {
          ...network,
          feeAmount: variantBalance.cryptoNetworks[0].feeAmount,
          minWithdrawalAmount: variantBalance.cryptoNetworks[0].minWithdrawalAmount
        };
      }
    }
    
    selectedNetwork = newNetwork;
    withdrawForm.cryptoNetworkName = newNetwork.name;
    
    // Delay funds check to avoid reactivity loop
    setTimeout(() => {
      loading = false;
      if (currentBalance?.name === 'USDT') {
        checkFunds(currentBalance);
      }
    }, 100);
  }

  async function checkFunds(crypto: IBalance) {
    if (!crypto || fundsLoading) return;
    
    fundsLoading = true;
    
    try {
      // For USDT, we need to check the specific network variant
      const cryptoName = crypto.name === 'USDT' 
        ? selectedNetwork?.name === 'ETH' ? 'USDTE' : 'USDTT'
        : crypto.shortName;

      if (!cryptoName) {
        fundsLoading = false;
        return;
      }

      const res = await userService.getUserWithdrawableFunds(cryptoName);
      
      if (res.data.status !== 200) {
        console.error('Failed to fetch withdrawable funds:', res.data);
        // Use fallback values
        withdrawableFunds = {
          availableFunds: crypto.balance || 0,
          lockedFunds: 0
        };
        return;
      }
      
      withdrawableFunds = res.data.data;
    } catch (error) {
      console.error('Error checking funds:', error);
      // Use fallback values from the balance if API fails
      withdrawableFunds = {
        availableFunds: crypto.balance || 0,
        lockedFunds: 0
      };
    } finally {
      fundsLoading = false;
    }
  }

  function setAmount(min: string) {
    if (!selectedNetwork) return;
    
    if (withdrawableFunds.availableFunds < selectedNetwork.minWithdrawalAmount) {
      toasts.send('Insufficient funds', 'error');
      return;
    }
    
    if (min === 'Min') {
      withdrawForm.amount = selectedNetwork.minWithdrawalAmount;
    } else {
      withdrawForm.amount = (withdrawableFunds.availableFunds * parseInt(min)) / 100;
    }
  }

  const altOnSubmit = createSubmitHandler({
    onSubmit: (values) => handleSubmit(),
  });

  const handleSubmit = async (): Promise<void> => {
    loading = true;
    apiError = '';

    try {
      // For USDT, use the specific network variant name
      const submitForm = {
        ...withdrawForm,
        cryptoName: withdrawForm.cryptoName === 'USDT' 
          ? selectedNetwork?.name === 'ETH' ? 'USDTE' : 'USDTT'
          : withdrawForm.cryptoName
      };

      const response = await fetch(`${environment.apiUrl}/user/withdrawal-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getCookie('forza_token')}`
        },
        body: JSON.stringify(submitForm)
      });
      
      const data = await response.json();
      
      if (data.status !== 200) {
        apiError = data.error;
        loading = false;
        return;
      }
      
      loading = false;
      clearForm();
      toasts.send('Withdrawal request has been submitted', 'success');
    } catch (err: any) {
      apiError = err.message || 'An error occurred. Please try again.';
      loading = false;
    }
  };

  const onLockedFundsClick = () => {
    openDialog('RolloverTableModal');
  };
</script>

{#if selectedBalance && selectedNetwork && storedBalances?.length > 0}
  <div class="text-xs w-full font-medium my-3 md:my-5">
    <p class="text-xs">
      You will receive the specified {selectedBalance.shortName} amount to your withdrawal address. Transaction
      fees apply.
    </p>
  </div>
  <form class="flex flex-col justify-center items-center w-full" use:form>
    <div class="flex gap-3 mb-4">
      <CustomDropdown 
        selected={selectedBalance} 
        selectedTitle="name" 
        items={storedBalances} 
        hasIcon={true}
        on:selected={(item) => selectBalance(item.detail)} 
        id="cryptoCurrencyDropdown"
      />
      {#if selectedBalance?.cryptoNetworks?.length > 0 && selectedNetwork}
      <CustomDropdown 
        selected={{
          ...selectedNetwork,
          name: getNetworkDisplayName(selectedNetwork)
        }}
        selectedTitle="name" 
        items={selectedBalance.cryptoNetworks.map(network => ({
          ...network,
          name: selectedBalance?.name === "USDT" 
            ? network.name === "ETH" ? "ERC20" : "TRC20"
            : network.name
        }))}
        hasIcon={false} 
        on:selected={(item) => {
          // Restore original network object from cryptoNetworks
          const originalNetwork = selectedBalance.cryptoNetworks?.find(
            n => n.name === (item.detail.name === "ERC20" ? "ETH" : "TRON")
          );
          if (originalNetwork) {
            selectNetwork(originalNetwork);
          }
        }}
        id="CryptoNetworkDropdown"
      />
    {/if}
    </div>

    <input type="hidden" bind:value={withdrawForm.cryptoName} name="cryptoName">
    <input type="hidden" bind:value={withdrawForm.cryptoNetworkName} name="cryptoNetworkName">

    <div class="w-full my-2 md:my-0 md:mt-2">
      <label class="text-primary-lightBlue ml-2" for="withdrawAddress">Address</label>
      <input 
        id="withdrawAddress" 
        name="address" 
        bind:value={withdrawForm.address} 
        type="text"
        placeholder="Wallet Address Here"
        class="w-full bg-primary-dark py-2 mt-2 px-5 rounded-full outline-0 placeholder-primary-lightBlue"
      >
    </div>
    <div class="w-full">
      {#if $errors.address}
        <span class="text-red-500 text-sm ml-3 animate-fadeIn">{$errors.address}</span>
      {/if}
    </div>

    <div class="{selectedBalance.shortName === 'XRP' ? 'block' : 'hidden'} w-full">
      <div class="md:mt-2">
        <label class="text-primary-lightBlue ml-2" for="destinationTag">Destination Tag</label>
        <input 
          id="destinationTag" 
          name="tag" 
          bind:value={withdrawForm.tag} 
          type="text"
          placeholder="Destination Tag Here"
          class="w-full bg-primary-dark py-2 mt-2 px-5 rounded-full outline-0 placeholder-primary-lightBlue"
        >
      </div>
      {#if $errors.tag}
        <span class="text-red-500 text-sm ml-3 animate-fadeIn">{$errors.tag}</span>
      {/if}
    </div>

    <div class="w-full md:mt-8 bg-primary-dark rounded-xl p-2">
      <label class="text-primary-lightBlue ml-2" for="withdrawAmount">
        Withdrawal Amount (Min
        <NumberFormat 
          value={selectedNetwork.minWithdrawalAmount}
          digitsInfo={selectedBalance.digitsInfo}
        /> {selectedBalance.name})
      </label>
      <input 
        id="withdrawAmount" 
        name="amount" 
        bind:value={withdrawForm.amount} 
        type="number" 
        placeholder="Amount"
        class="w-full bg-primary-blue py-2 mt-2 px-5 rounded-lg outline-0 placeholder-primary-lightBlue"
      >
      <div class="grid grid-cols-4 mt-2 gap-2 text-sm text-primary-yellow">
        <button 
          type="button"
          class="col-span-1 bg-primary-blue/40 hover:bg-primary-blue/80 p-2 rounded-lg"
          on:click={() => setAmount('Min')}
        >
          Min
        </button>
        <button 
          type="button"
          class="col-span-1 bg-primary-blue/40 hover:bg-primary-blue/80 p-2 rounded-lg"
          on:click={() => setAmount('25')}
        >
          25%
        </button>
        <button 
          type="button"
          class="col-span-1 bg-primary-blue/40 hover:bg-primary-blue/80 p-2 rounded-lg"
          on:click={() => setAmount('50')}
        >
          50%
        </button>
        <button 
          type="button"
          class="col-span-1 bg-primary-blue/40 hover:bg-primary-blue/80 p-2 rounded-lg"
          on:click={() => setAmount('100')}
        >
          Max
        </button>
      </div>
      <div class="flex justify-between text-sm mt-2 px-2">
        <div class="text-primary-lightBlue">
          Available: <span class="text-primary-fire">
            <NumberFormat 
              value={withdrawableFunds.availableFunds}
              digitsInfo={selectedBalance.digitsInfo}
            /> {selectedBalance.name}
          </span>
          {#if fundsLoading}
            <span class="ml-1 inline-block animate-pulse">...</span>
          {/if}
        </div>
        <div class="text-primary-lightBlue">
          <span class="underline cursor-pointer" on:click={onLockedFundsClick}>Locked:</span> 
          <span class="text-primary-fire">
            <NumberFormat
              value={withdrawableFunds.lockedFunds}
              digitsInfo={selectedBalance.digitsInfo}
            /> {selectedBalance.name}
          </span>
        </div>
      </div>
    </div>
    <div class="w-full">
      {#if $errors.amount}
        <span class="text-red-500 text-sm ml-3 animate-fadeIn">{$errors.amount}</span>
      {/if}
    </div>

    <div class="border-t border-primary-lightBlue w-full mt-4 flex flex-col gap-2 py-3 text-sm">
      <div class="flex justify-between text-primary-fire">
        <span>Withdrawal Amount</span>
        <span class="font-bold">{withdrawForm.amount ?? 0} {withdrawForm.cryptoName}</span>
      </div>
      <div class="flex justify-between">
        <span>Fee</span>
        <span class="font-bold">
          <NumberFormat 
            value={selectedNetwork.feeAmount}
            digitsInfo={selectedBalance.digitsInfo}
          /> {selectedBalance.name}
        </span>
      </div>
      <div class="flex justify-between">
        <span>Total Withdraw amount</span>
        <span class="font-bold">
          <NumberFormat
            value={(withdrawForm.amount - selectedNetwork.feeAmount) < 0 ? 0 : (withdrawForm.amount - selectedNetwork.feeAmount)}
            digitsInfo="1.2-8"
          /> {selectedBalance.name}
        </span>
      </div>
    </div>

    {#if apiError}
      <div class="min-h-6 animate-fadeIn">
        <div class="px-4 py-3 my-2 rounded-full border border-red-500 text-red-500 text-sm">
          <div>{$translate(apiError)}</div>
        </div>
      </div>
    {/if}

    <Button 
      on:click={altOnSubmit} 
      {loading}
      clas="w-full shadow-xl mt-8"
      disabled={withdrawableFunds.availableFunds < withdrawForm.amount || 
                withdrawForm.amount < (selectedNetwork?.minWithdrawalAmount || 0) || 
                !$isValid}
    >
      Withdraw
    </Button>

    <div class="bg-primary-yellow/10 text-xs rounded-xl p-2 mt-4">
      For Security purposes, large or suspicious withdrawals may require additional time to process. We appreciate
      your patience.
    </div>
  </form>
{:else}
  <div class="flex justify-center items-center p-4">
    <span class="text-primary-lightBlue">Loading...</span>
  </div>
{/if}

<style>
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-in-out;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
  
  .animate-pulse {
    animation: pulse 1.5s infinite;
  }
</style>